/* eslint-disable indent */
/* eslint-disable no-console */
import React, { useEffect, useState, useCallback } from 'react';
import TrucksMapDialogStyles from './TrucksMapDialog.component.styles';
import { compose } from 'recompose';
import { bindActionCreators } from 'redux';
import cn from 'classnames';
import { Typography, Slide, Dialog, AppBar, Snackbar, Toolbar, Grid, IconButton } from '@material-ui/core';
import { withGlobalStyles } from 'Support/Theme/CreateInjectableStyles/withGlobalStyles';
import CloseIcon from '@material-ui/icons/Close';
import withServices from '../../Contexts/Services/WithServices.context';
import CircularProgress from '@material-ui/core/CircularProgress';
import ErrorIcon from '@material-ui/icons/Error';
import { empty } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { connect } from 'react-redux';
import uiActionCreators from '../../State/Store/UI/UI.actions';
import TrucksMap from '../TrucksMap/TrucksMap.component';
import { MFSDR } from 'Constants/MouseFlow/SensitiveDataReplacer';

const TrucksMapDialog = ({ classes, open, onClose, services, currentOrder, reduxState }) => {
  const secondsToRefresh = 10;

  const [counter, setCounter] = useState(10);
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [trucksLoading, setTrucksLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // New state for initial load

  const [currentTrucks, setCurrentTrucks] = useState({
    truckCount: 0,
    trucksArrived: 0,
    site: { address: '', location: { lat: 0, lng: 0 } },
    lastUpdated: '',
    plants: [],
    trucks: [],
    _invalidTrucks: 0,
  });

  const requestTrucksForMap = useCallback(() => {
    // Helper functions defined inside to avoid dependency issues
    const transformDeliveryDataToTrucksMap = (deliveryData) => {
      console.log('🔍 RAW API Response:', deliveryData);

      // Check for direct orders structure (which matches your API response)
      if (deliveryData?.orders) {
        console.log('✅ Found orders directly in response');
        const { orders } = deliveryData;
        return processOrdersData(orders);
      }

      // Check for nested data.orders structure
      if (deliveryData?.data?.orders) {
        console.log('✅ Found orders in data.orders structure');
        const { orders } = deliveryData.data;
        return processOrdersData(orders);
      }

      console.log('❌ No valid orders structure found in response');
      return null;
    };

    const processOrdersData = (orders) => {
      console.log('🏗️ Processing orders data:', orders);

      const { destination, shipAddress, loads } = orders;

      // Extract site information - even if no trucks, we want to show the site
      const site = {
        address: shipAddress?.trim(),
        location: {
          lat: destination?.lat,
          lng: destination?.long,
        },
      };

      // Extract unique plants from in-transit loads only
      const plantsMap = new Map();
      const inTransitLoads = loads?.intransit || [];

      console.log(`🚛 Found ${inTransitLoads.length} in-transit loads`);

      inTransitLoads.forEach(load => {
        if (load.plant) {
          const plantKey = load.plantCode;
          if (!plantsMap.has(plantKey)) {
            const plant = {
              plantNumber: load.plantCode,
              name: load.plant.name,
              address: null,
              lat: load.plant.lat,
              lng: load.plant.long,
              trucks: [],
            };
            plantsMap.set(plantKey, plant);
          }
        }
      });

      // Extract only in-transit trucks
      const trucks = [];
      inTransitLoads.forEach((load, index) => {
        const truck = load.truck;

        if (truck && truck.status === 'INTRANSIT' && truck.Lat && truck.Long) {
          const truckData = {
            id: truck.deviceId || truck.code,
            lat: truck.Lat,
            lng: truck.Long,
            status: truck.status,
            eta: truck.etaFormattedTime || truck.eta,
            code: truck.code,
            deviceId: truck.deviceId,
            loadNumber: load.loadNumber,
            ticketCode: load.ticketCode,
            truckNumber: truck.code,
            // Add additional data to prevent timestamp errors
            loadQty: load.loadQty,
            reviewedOnJobTime: load.reviewedOnJobTime,
            etaCalculatedAt: truck.etaCalculatedAt,
          };
          trucks.push(truckData);
        }
      });

      const result = {
        truckCount: trucks.length,
        trucksArrived: 0, // We only show in-transit, so none have arrived yet
        site,
        lastUpdated: new Date().toISOString(),
        plants: Array.from(plantsMap.values()),
        trucks,
      };

      console.log('🎯 Final transformation result:', {
        trucksCount: result.trucks.length,
        plantsCount: result.plants.length,
        site: result.site.address,
      });

      return result;
    }; const isInvalidTruck = (truck) => !truck.lat || !truck.lng || truck.eta === 'N/A';

    const getInvalidTrucks = (trucks) => {
      return trucks.filter((truck) => {
        if (isInvalidTruck(truck)) {
          return true;
        }
        return false;
      });
    };

    if (counter >= secondsToRefresh || initialLoading) {
      setCounter(0);
      setTrucksLoading(true);

      (async () => {
        try {
          const { summary } = currentOrder || {};
          if (!summary) {
            setTrucksLoading(false);
            setInitialLoading(false);
            return;
          }
          const { orderCode, orderLine } = summary;

          // Use the existing delivery details API instead of broken trucks map API
          const deliveryDetails = await services.orderService.findByStatus({
            orderCode,
            orderLine,
            status: 'DELIVERED,INTRANSIT,SCHEDULED',
            state: reduxState,
          });

          const transformedData = transformDeliveryDataToTrucksMap(deliveryDetails);

          if (!transformedData) {
            setErrorVisible(true);
            setErrorMessage('No delivery data available');
            setTrucksLoading(false);
            setInitialLoading(false);
            return;
          }

          const invalidTrucks = getInvalidTrucks(transformedData.trucks);

          if (invalidTrucks.length) {
            const invalidTrucksMessage = invalidTrucks.reduce((acc, el, i) => {
              if (invalidTrucks.length === 1) {
                return el.truckNumber;
              } else if (invalidTrucks.length - 2 === i) {
                // second last element so add "and"
                acc = acc + el.truckNumber + ' and ';
              } else if (invalidTrucks.length - 1 === i) {
                // last element
                acc += el.truckNumber;
              } else {
                acc = acc + el.truckNumber + ', ';
              }
              return acc;
            }, '');
            setErrorVisible(true);
            setErrorMessage(`Truck ${invalidTrucksMessage} cannot be displayed on the map at this time`);
          } else {
            setErrorVisible(false);
          }

          const validResult = {
            ...transformedData,
            _invalidTrucks: invalidTrucks.length,
            trucks: transformedData.trucks.filter((truck) => !isInvalidTruck(truck)),
          };

          setCurrentTrucks(validResult);
          setInitialLoading(false); // Clear initial loading state
        } catch (ex) {
          setErrorVisible(true);
          setErrorMessage('Something went wrong loading trucks for map');
          setInitialLoading(false); // Clear initial loading state even on error
        }

        setTrucksLoading(false);
      })();
    } else {
      setCounter(counter + 1);
    }
  }, [counter, currentOrder, services.orderService, reduxState, initialLoading]);

  useEffect(() => {
    // make initial request immediately when dialog opens
    if (open) {
      setInitialLoading(true); // Set initial loading state
      // Don't clear currentTrucks to null, keep the previous data
      setCounter(secondsToRefresh); // Set counter to trigger immediate API call
      requestTrucksForMap();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCounter(0);
      setErrorVisible(false);
      setInitialLoading(false);
      // Don't clear currentTrucks here - let it keep the last valid data
      return;
    }

    const timeout = setTimeout(requestTrucksForMap, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [open, counter]);

  return (
    <Dialog className={classes.dialogWrapper} open={open} onClose={onClose} TransitionComponent={Transition} fullScreen>
      <Snackbar
        action={[
          <IconButton aria-label="Close" className={classes.close} color="inherit" key="close" onClick={() => setErrorVisible(false)}>
            <CloseIcon />
          </IconButton>,
        ]}
        anchorOrigin={{
          horizontal: `left`,
          vertical: `bottom`,
        }}
        className={classes.snackbar}
        ContentProps={{ classes: { root: classes.error } }}
        message={
          <span className={classes.snackbarMessage} id="client-snackbar" data-mf-replace-inner={MFSDR}>
            <ErrorIcon className={classes.snackbarIcon} />
            {errorMessage}
          </span>
        }
        open={errorVisible}
      />
      <AppBar classes={{ root: classes.appBar }}>
        <Toolbar>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <IconButton color="inherit" onClick={onClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Typography variant="h6" color="inherit">
                Trucks Map {initialLoading ? '(Loading...)' : ''}
              </Typography>
            </Grid>
            <Grid item className={cn(classes.flexGrow1, classes.textAlignRight)}>
              {!initialLoading && (
                <CircularProgress
                  size={32}
                  className={cn(classes.circularProgress)}
                  value={Math.floor((counter / secondsToRefresh) * 100)}
                  variant="static"
                />
              )}
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Loading overlay for initial data fetch */}
      {initialLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999,
        }}>
          <CircularProgress size={60} style={{ color: '#fff' }} />
          <Typography variant="h6" style={{ marginTop: 16, color: '#fff' }}>
            Loading trucks map...
          </Typography>
        </div>
      )}

      {/* Always render the map, but show loading overlay during initial load */}
      <TrucksMap trucksInfo={currentTrucks} />
    </Dialog>
  );
};

function Transition(props) {
  return <Slide direction="up" {...props} />;
}

export default compose(withGlobalStyles(TrucksMapDialogStyles), withServices,
connect(
  (state) => ({
    reduxState: state,
  }),
  (dispatch) => ({
    actions: bindActionCreators(
      {
        ...uiActionCreators,
      },
      dispatch,
    ),
  }),
))(TrucksMapDialog);
